"use client";

import type { AuthAuditEvent, ExternalToken } from "@webuild-ai/react-auth";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { logger } from "@/telemetry";
import { isAllowedParentOrigin } from "@/config/allowedParentOrigins";

/**
 * Embedded (iframe) SSO for hosting Pathway inside a trusted parent application such as
 * SharePoint. See docs/SHAREPOINT_IFRAME_SSO.md and react-auth ADR-0008.
 *
 * This provider runs the postMessage handshake with the parent window and feeds the
 * result into `@webuild-ai/react-auth` (via AuthUserProvider's `embedded` / `loginHint` /
 * `externalToken` / `onInteractionRequired` / `onAuthEvent` props). Because react-auth's
 * `useAuth()` then reports authenticated from the injected token, every existing consumer
 * (the API clients, the SSE client, `authenticatedFetch`, route guards) keeps working
 * unchanged.
 *
 * Two-tier authentication:
 *   tier 1 - the parent sends the user's email (USER_INFO); react-auth attempts MSAL
 *            ssoSilent with it. Azure AD remains the authority.
 *   tier 2 - the parent sends a pre-acquired access token (AUTH_TOKEN); used as a
 *            fallback when ssoSilent cannot complete (third-party cookies blocked).
 *
 * SECURITY: every inbound message is validated against an origin allow-list
 * (`isAllowedParentOrigin`) and `event.source`. The token is held in memory only and is
 * never persisted or logged. Outbound control messages (AUTH_REQUEST, REQUEST_LOGOUT)
 * carry no secret, so they use a "*" target origin; only inbound messages are trusted.
 */

/** Milliseconds to wait for the parent to respond before declaring failure. */
const PARENT_AUTH_TIMEOUT_MS = 8000;

/** Fallback token lifetime if the parent omits `expiresAt`. */
const DEFAULT_TOKEN_TTL_MS = 60 * 60 * 1000;

export type EmbeddedAuthStatus = "idle" | "resolving" | "ready" | "failed" | "logged-out";

/** Inbound AUTH_TOKEN message shape (parent to child). */
interface AuthTokenMessage {
  type: "AUTH_TOKEN";
  accessToken: string;
  expiresAt?: number;
  user?: { sub?: string; id?: string; name?: string; email?: string };
}

/** Inbound AUTH_REFRESH message shape (parent to child). */
interface AuthRefreshMessage {
  type: "AUTH_REFRESH";
  accessToken: string;
  expiresAt?: number;
}

/** Inbound USER_INFO message shape (parent to child) - supplies the ssoSilent loginHint. */
interface UserInfoMessage {
  type: "USER_INFO";
  user?: { name?: string; email?: string; initials?: string };
}

export interface EmbeddedAuthConfig {
  /** True when Pathway is running inside an iframe. */
  isEmbedded: boolean;
  /** Handshake status (idle when not embedded). */
  status: EmbeddedAuthStatus;
  /** Email hint for react-auth ssoSilent (tier 1). */
  loginHint?: string;
  /** Injected access token for react-auth (tier 2). */
  externalToken: ExternalToken | null;
  /** Set when the handshake failed (e.g. parent never responded). */
  error: string | null;
  /** Ask the parent to (re)send authentication. */
  requestAuth: () => void;
  /** Passed to react-auth: called when it needs a fresh token. */
  onInteractionRequired: () => void;
  /** Passed to react-auth: receives the PII-free audit trail. */
  onAuthEvent: (event: AuthAuditEvent) => void;
}

const NOOP = () => {};

const EmbeddedAuthContext = createContext<EmbeddedAuthConfig>({
  isEmbedded: false,
  status: "idle",
  externalToken: null,
  error: null,
  requestAuth: NOOP,
  onInteractionRequired: NOOP,
  onAuthEvent: NOOP,
});

/** Access the embedded auth configuration. Returns inert defaults when not embedded. */
export const useEmbeddedAuthConfig = (): EmbeddedAuthConfig => useContext(EmbeddedAuthContext);

/** Detect whether the app is running inside an iframe. */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin parent throws on access, which itself means we are embedded.
    return true;
  }
}

export const EmbeddedAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const embedded = useMemo(() => isInIframe(), []);
  const [externalToken, setExternalToken] = useState<ExternalToken | null>(null);
  const [loginHint, setLoginHint] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<EmbeddedAuthStatus>(embedded ? "resolving" : "idle");
  const [error, setError] = useState<string | null>(null);
  // True once the parent has responded at all (token or hint).
  const receivedRef = useRef(false);

  const postToParent = useCallback((message: Record<string, unknown>) => {
    // Control messages carry no secret. We do not know the validated parent origin until
    // an inbound message arrives, so "*" is acceptable here; all INBOUND messages are
    // origin-validated before use.
    try {
      window.parent?.postMessage(message, "*");
    } catch {
      logger.warn("[embedded-auth] failed to post message to parent");
    }
  }, []);

  const requestAuth = useCallback(() => {
    if (!embedded) return;
    postToParent({ type: "AUTH_REQUEST" });
  }, [embedded, postToParent]);

  const onInteractionRequired = useCallback(() => {
    if (!embedded) return;
    logger.info("[embedded-auth] interaction required - requesting token from parent");
    setStatus((current) => (current === "ready" ? current : "resolving"));
    requestAuth();
  }, [embedded, requestAuth]);

  const onAuthEvent = useCallback(
    (event: AuthAuditEvent) => {
      // Audit events are PII-free and token-free by contract; forward to telemetry.
      logger.info("[embedded-auth][audit]", {
        type: event.type,
        mode: event.mode ?? "",
        userId: event.userId ?? "",
        ...(event.detail ?? {}),
      });

      // react-auth signals logout in embedded mode by emitting this event. Ask the parent
      // to perform the actual sign-out; never navigate the IdP inside the iframe.
      if (event.type === "embedded_logout") {
        postToParent({ type: "REQUEST_LOGOUT" });
      }
    },
    [postToParent],
  );

  useEffect(() => {
    if (!embedded) return;

    const handleMessage = (event: MessageEvent) => {
      if (!isAllowedParentOrigin(event.origin)) {
        logger.warn("[embedded-auth] rejected message from unauthorised origin", {
          origin: event.origin,
        });

        return;
      }

      // Only accept messages from our actual parent window. A null source (synthetic
      // events, sandboxed frames without allow-same-origin) is rejected too.
      if (event.source !== window.parent) {
        return;
      }

      const data = event.data as { type?: unknown };

      if (!data || typeof data.type !== "string") {
        return;
      }

      switch (data.type) {
        case "AUTH_TOKEN": {
          const message = event.data as AuthTokenMessage;

          if (typeof message.accessToken !== "string" || !message.accessToken.trim()) return;

          let user: ExternalToken["user"];

          if (message.user) {
            const id = String(message.user.sub ?? message.user.id ?? "").trim();

            if (!id) {
              logger.warn("[embedded-auth] rejected AUTH_TOKEN with no user id");

              return;
            }
            user = { id, name: message.user.name, email: message.user.email };
          }

          receivedRef.current = true;
          setExternalToken({
            accessToken: message.accessToken,
            expiresAt:
              typeof message.expiresAt === "number"
                ? message.expiresAt
                : Date.now() + DEFAULT_TOKEN_TTL_MS,
            user,
          });
          const tokenHint = message.user?.email?.trim();

          if (tokenHint) {
            setLoginHint(tokenHint);
          }
          setError(null);
          setStatus("ready");

          return;
        }

        case "AUTH_REFRESH": {
          const message = event.data as AuthRefreshMessage;

          if (typeof message.accessToken !== "string" || !message.accessToken.trim()) return;

          receivedRef.current = true;
          // Construct a token even if none was held yet (e.g. refresh arrives first),
          // so a late refresh can always recover a usable session.
          setExternalToken((previous) => ({
            accessToken: message.accessToken,
            expiresAt:
              typeof message.expiresAt === "number"
                ? message.expiresAt
                : (previous?.expiresAt ?? Date.now() + DEFAULT_TOKEN_TTL_MS),
            user: previous?.user,
          }));
          setError(null);
          setStatus("ready");

          return;
        }

        case "USER_INFO": {
          const message = event.data as UserInfoMessage;
          const hint = message.user?.email?.trim();

          // A USER_INFO without a usable email hint is a no-op: receivedRef stays false
          // so the initial-handshake timeout still fires if no token/hint ever arrives.
          if (hint) {
            receivedRef.current = true;
            // Tier 1: hand the hint to react-auth so it can attempt ssoSilent.
            setLoginHint(hint);
          }

          return;
        }

        case "AUTH_LOGOUT": {
          // Mark as received so the initial-handshake timeout does not also fire.
          receivedRef.current = true;
          setExternalToken(null);
          setLoginHint(undefined);
          setError(null);
          setStatus("logged-out");

          return;
        }

        default:
          // THEME_CHANGE / LANGUAGE_CHANGE etc. are not auth concerns; ignore here.
          return;
      }
    };

    window.addEventListener("message", handleMessage);
    requestAuth();

    const timeout = window.setTimeout(() => {
      if (!receivedRef.current) {
        setStatus("failed");
        setError("Timed out waiting for authentication from the parent application.");
        logger.warn("[embedded-auth] timed out waiting for parent authentication");
      }
    }, PARENT_AUTH_TIMEOUT_MS);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.clearTimeout(timeout);
    };
  }, [embedded, requestAuth]);

  const value = useMemo<EmbeddedAuthConfig>(
    () => ({
      isEmbedded: embedded,
      status,
      loginHint,
      externalToken,
      error,
      requestAuth,
      onInteractionRequired,
      onAuthEvent,
    }),
    [
      embedded,
      status,
      loginHint,
      externalToken,
      error,
      requestAuth,
      onInteractionRequired,
      onAuthEvent,
    ],
  );

  return <EmbeddedAuthContext.Provider value={value}>{children}</EmbeddedAuthContext.Provider>;
};
